import React, {ChangeEvent, useEffect, useState} from "react";
import OrderCountHistogram from "./OrderCountHistogram";
import moment from "moment";
import {useGetEnumsQuery} from "../../../../app/globalApi";
import {useGetAllRegularCustomersQuery} from "../../../../features/customer/customerApi";
import InputField from "../../form/InputField";
import SelectField from "../../form/SelectField";
import {createOptionListForSelectTag} from "../../../../helpers/utils/Utils";
import {OptionType} from "../../../../helpers/utils/StyleUtils";
import {useUtilDataQuery} from "../../../../features/profile/profileApi";
import ButtonTray from "../../form/ButtonTray";

interface BarSearchPerimeters {
    customerId: string | null;
    period: string | null;
    creationDateFrom: string | null;
    creationDateTo: string | null;
    isPageLoad?: boolean;
}

export const OrderCountHistogramSearchForm: React.FC = () => {
    const customerData = useGetAllRegularCustomersQuery("");
    const {data: utilData} = useUtilDataQuery("");

    const defaultPeriod = "Days";
    const searchParamsData = localStorage.getItem("orderCountHistogramSearchParams");
    const {data: enums} = useGetEnumsQuery("");
    const [period, setPeriod] = useState<any>();
    const [customerId, setCustomerId] = useState<any>();
    const [creationDateFrom, setCreationDateFrom] = useState<string | null>(null);
    const [creationDateTo, setCreationDateTo] = useState<string | null>(null);
    const [applySearch, setApplySearch] = useState<boolean>(false);
    const [applyExport, setApplyExport] = useState<boolean>(false);
    const [showHideSearchForm, setShowHideSearchForm] = useState<boolean>(false);
    const [barSearchPerimeters, setBarSearchPerimeters] = useState<BarSearchPerimeters>({
        customerId: null,
        period: null,
        creationDateFrom: null,
        creationDateTo: null,
        isPageLoad: true,
    });


    useEffect(() => {
        const parsedSearchParams = searchParamsData ? JSON.parse(searchParamsData) : null;
        const customerId =
            utilData?.customerLevel === "CUSTOMER"
                ? utilData?.customer_Id
                : parsedSearchParams?.customerId || null;
        setCustomerId(customerId);
        setPeriod({value: defaultPeriod?.toUpperCase(), label: defaultPeriod});
        setCreationDateFrom(moment(computeDefaultStartDate(defaultPeriod?.toUpperCase())).format("YYYY-MM-DD"));
        setCreationDateTo(moment(computeDefaultEndDate(defaultPeriod?.toUpperCase())).format("YYYY-MM-DD"));
        setApplySearch(true);
    }, []);


    useEffect(() => {

        if (utilData !== null && utilData !== undefined && utilData?.customerLevel !== null && utilData?.customerLevel !== '' && barSearchPerimeters.isPageLoad === false && utilData?.orderCountBarChartParams === null) {
            setApplySearch(true);
        }
    }, []);

    const computeDefaultStartDate = (period: string): Date => {
        const defaultDate = new Date();
        switch (period) {
            case "WEEKS":
                return new Date(defaultDate.setDate(defaultDate.getDate() - 30));
            case "MONTHS":
                return new Date(defaultDate.setFullYear(defaultDate.getFullYear() - 1));
            default:
                return new Date(defaultDate.getFullYear(), defaultDate.getMonth(), 1);
        }
    }

    const computeDefaultEndDate = (period: string): Date => {
        const defaultDate = new Date();
        return period === "DAYS"
            ? new Date(defaultDate.getFullYear(), defaultDate.getMonth() + 1, 0, 23, 59, 59)
            : defaultDate;
    }


    const handleClick = (
        e: React.MouseEvent<HTMLButtonElement>,
        setState: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        e.preventDefault();
        setBarSearchPerimeters({
            customerId,
            period: period?.value,
            creationDateFrom,
            creationDateTo,
        });
        setState(true);
    }

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | string | null }) => {
        const {name, value} = "target" in e ? e.target : e;
        if (name === "customerId") {
            setCustomerId(value ? (typeof value === 'string' ? value : value?.value) : null);
        } else if (name === "period") {
            setPeriod(value);
            if (value && typeof value === 'object') {
                initializePeriodFields(value.value);
            }
        }
    };

    const clearForm = () => {
        initializePeriodFields();
        setCustomerId(utilData?.customerLevel === 'CUSTOMER' ? utilData?.customer_Id : null);
    }

    const initializePeriodFields = (newPeriod = defaultPeriod) => {
        const periodUpper = newPeriod?.toUpperCase?.() || defaultPeriod;

        let fromDate: string;
        let toDate: string;

        if (periodUpper === "DAYS") {
            const start = moment().startOf("month");
            const end = moment().endOf("month");
            fromDate = start.format("YYYY-MM-DD");
            toDate = end.format("YYYY-MM-DD");
        } else if (periodUpper === "WEEKS") {
            const start = moment().subtract(30, "days");
            const end = moment(); // today
            fromDate = start.format("YYYY-MM-DD");
            toDate = end.format("YYYY-MM-DD");
        } else if (periodUpper === "MONTHS") {
            const start = moment().subtract(12, "months");
            const end = moment(); // today
            fromDate = start.format("YYYY-MM-DD");
            toDate = end.format("YYYY-MM-DD");
        } else {
            // Fallback to DAYS logic
            const start = moment().startOf("month");
            const end = moment().endOf("month");
            fromDate = start.format("YYYY-MM-DD");
            toDate = end.format("YYYY-MM-DD");
        }

        setPeriod({value: periodUpper, label: newPeriod});
        setCreationDateFrom(fromDate);
        setCreationDateTo(toDate);
        setApplySearch(true);
    };


    return (
        <>
            <div className="dashboard__bar_chart ">
                <div className="pie_chart">
                    <OrderCountHistogram
                        period={period?.value as string}
                        customerId={customerId ?? ""}
                        creationDateFrom={creationDateFrom ?? ""}
                        creationDateTo={creationDateTo ?? ""}
                        applySearch={Boolean(applySearch)}
                        setApplySearch={setApplySearch}
                        applyExport={Boolean(applyExport)}
                        setApplyExport={setApplyExport}
                        enums={enums?.["ProductType"]}
                        utilData={utilData}
                    />
                </div>
            </div>

            <i
                className="fas fa-cog fa-icon-color cursor_hover mb-3 text-highlight-0"
                onClick={() => setShowHideSearchForm(!showHideSearchForm)}
                title={showHideSearchForm ? "Hide search form" : "Show search form"}
            />

            {showHideSearchForm && (
                <div>
                    <div className="flex justify-between gap-2">
                        <div className={"w-full"}>
                            <SelectField
                                placeholder='Select customer...'
                                value={
                                    customerId
                                        ? createOptionListForSelectTag(customerData?.data, "customerName", "customerName")
                                            ?.find(option => option.value === customerId)
                                        : ""
                                }
                                options={createOptionListForSelectTag(customerData?.data, "customerName", "id")}
                                onChange={(selectedOption) => {
                                    handleOnChange({name: "customerId", value: selectedOption});
                                }}
                                label={"Customer"}
                                labelPosition={"top"}
                            />
                        </div>

                        <div className={"w-full"}>
                            <SelectField
                                placeholder='Select Period...'
                                value={period}
                                options={createOptionListForSelectTag(enums?.["Period"], "value", "constant")}
                                onChange={(selectedOption) => {
                                    handleOnChange({name: "period", value: selectedOption});
                                }}
                                label={"Period"}
                                labelPosition={"top"}
                            />
                        </div>

                        <div className={"w-full"}>
                            <InputField
                                label="Created Date - From"
                                name="creationDateFrom"
                                value={creationDateFrom || ""}
                                type="date"
                                onChange={(e) => setCreationDateFrom(e.target.value)}
                                className="mb-2 w-full py-2 px-3 text-lg"
                                labelPosition="top"
                            />
                        </div>
                        <div className={"w-full"}>
                            <InputField
                                label="Created Date - To"
                                name="creationDateTo"
                                value={creationDateTo || ""}
                                type="date"
                                onChange={(e) => setCreationDateTo(e.target.value)}
                                className="mb-2 w-full py-2 px-3 text-lg"
                                labelPosition="top"

                            />
                        </div>
                    </div>

                    <div className={"align-bottom mt-3 chart_bottom"}>
                        <ButtonTray
                            buttons={[
                                {
                                    buttonProps: {
                                        btnText: "Export",
                                        type: "submit",
                                        onClick: (e) => handleClick(e, setApplyExport),
                                        isLoading: (applyExport),
                                    },

                                    buttonType: "button",
                                },
                                {
                                    buttonProps: {
                                        btnText: "Clear",
                                        onClick: () => clearForm(),
                                        type: "close",
                                    },
                                    buttonType: "button"
                                },

                                {
                                    buttonProps: {
                                        btnText: "Apply",
                                        type: "submit",
                                        onClick: (e) => handleClick(e, setApplySearch),

                                        isLoading: (applySearch),
                                    },
                                    buttonType: "button",
                                }
                            ]}
                            align="start"
                            gap={2}
                        />
                    </div>

                </div>
            )}
        </>
    );
};

